import {
  ageDecadeFormatter,
  genderI18n,
  getReviewImagePath
} from '@lifedot/utils'
import { useShortCodeDataCemeteries } from '@/hooks/use-short-code-data/use-short-code-data'
import { Rating } from '@lifedot/atoms/Rating'
import Image from 'next/image'
import { FC } from 'react'
import { router } from '@lifedot/router'
import Link from 'next/link'
import { css } from '@emotion/react'
import { palette } from '@lifedot/styles/palette'
import { typography } from '@lifedot/styles/typography'
import { mq } from '@lifedot/styles/mediaQuery'
import { Frame } from '@lifedot/components/Frame'

const styles = {
  review: css({
    p: {
      margin: 0
    }
  }),
  header: css({
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    [mq('sp')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  }),
  title: css({
    color: palette.gray6,
    fontWeight: 'bold'
  }),
  score: css({
    display: 'flex',
    alignItems: 'center',
    marginLeft: 20,
    [mq('sp')]: {
      margin: 0
    }
  }),
  count: css({
    color: palette.main03
  }),
  reviewer: css({
    textAlign: 'center',
    width: 68
  }),
  reviewerInfo: css({
    fontWeight: 'bold',
    marginTop: 4
  }),
  comment: css({
    backgroundColor: palette.white,
    padding: 12,
    borderRadius: 4,
    border: `1px solid ${palette.gray2}`,
    position: 'relative',
    ':before': {
      content: '""',
      position: 'absolute',
      top: 20,
      left: -20,
      border: '8px solid transparent',
      borderRight: `12px solid ${palette.gray2}`
    },
    ':after': {
      content: '""',
      position: 'absolute',
      top: 20,
      left: -18,
      border: '8px solid transparent',
      borderRight: `12px solid ${palette.white}`
    }
  }),
  more: css({
    marginLeft: 4
  })
}

interface ReviewProps {
  cemeteryId: number
}

export const Review: FC<ReviewProps> = ({ cemeteryId }) => {
  const [cemetery] = useShortCodeDataCemeteries([cemeteryId])
  if (!cemetery) return null

  const { review_summary: reviewSummary } = cemetery
  const { valuable_review: valuableReview } = reviewSummary

  if (valuableReview === null) return null

  const path = getReviewImagePath({ ...valuableReview })
  const { age, gender, answers } = valuableReview

  return (
    <div css={styles.review}>
      <div css={styles.header}>
        <p css={styles.title}>みんなの口コミ評価</p>
        <div css={styles.score}>
          <Rating point={reviewSummary.total_score ?? 0} />
          <span css={typography.textS}>
            （
            <Link
              href={router.review(cemeteryId, reviewSummary.count)}
              passHref
              prefetch={false}
            >
              <a css={styles.count}>{reviewSummary.count}件</a>
            </Link>
            ）
          </span>
        </div>
      </div>
      <Frame
        leftContent={
          <div css={styles.reviewer}>
            <Image
              src={path}
              alt={`${ageDecadeFormatter(age)}・${genderI18n(gender)}`}
              width={48}
              height={48}
            />
            <p
              css={[typography.textXS, styles.reviewerInfo]}
            >{`${ageDecadeFormatter(age)}・${genderI18n(gender)}`}</p>
          </div>
        }
        rightContent={
          <p css={[typography.textS, styles.comment]}>
            {answers.find(({ code }) => code === 'facilities')?.text}…
            <Link
              href={router.review(cemeteryId, reviewSummary.count)}
              passHref
              prefetch={false}
            >
              <a css={styles.more}>続きを読む</a>
            </Link>
          </p>
        }
      />
    </div>
  )
}
